import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useTranslation } from 'next-i18next';
import React, { ReactElement, useState } from 'react';
import styles from './my-loyalty-vouchers.module.scss';
import VouchersPartnerView from '@utmb/types/view/VouchersPartnerView';
import { Image } from '@utmb/design-system/image/Image';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { Link } from '@utmb/design-system/link/link';
import { FetchMethodsEnum, TENANCY_HTTP_HEADER } from '@utmb/utils/utmb';
import { useUser } from '@utmb/contexts/AuthContext';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { toast } from 'react-toastify';
import Spinner from '@utmb/design-system/icons/svgs/Spinner';
import VoucherView from '@utmb/types/view/VoucherView';
import { LoyaltyPartnerTypeEnum } from '@utmb/types/enum/LoyaltyPartnerTypeEnum';
import { Font } from '@utmb/design-system/text/font';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import { LoyaltyLevelEnum } from '@utmb/types/enum/LoyaltyLevelEnum';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import { HubspotContactFormModal } from '../contact-form/hubspot-contact-form-modal/HubspotContactFormModal';

const MyLoyaltyVouchers = (): ReactElement => {
    const { dashboardMember } = useDashboard();

    const { t } = useTranslation('myutmb');

    const copyCode = (txt: string): void => {
        navigator.clipboard.writeText(txt);
    };

    return (
        <div className={styles.container}>
            <div
                className={styles.congrats}
                dangerouslySetInnerHTML={{ __html: t('myUtmb.vouchers.congrats', { name: dashboardMember?.information?.firstName ?? '' }) }}
            />
            <div className={styles.wrapVoucher}>
                {dashboardMember?.vouchersPartners?.map((p) => {
                    if (!p.vouchers) p.vouchers = [];
                    const currentVoucher = p.vouchers.find((v) => v.level === dashboardMember?.loyalty?.currentLevel);
                    if (!currentVoucher)
                        p.vouchers.unshift({ code: '', discount: p.discount, expireDate: '', level: dashboardMember?.loyalty?.currentLevel ?? 4 });
                    if (p.vouchers.some((target) => target.discount) && p.vouchers.some((target) => !target.discount))
                        p.vouchers = p.vouchers.filter((target) => target.code);
                    return p.vouchers.map((v) => <PartnerVoucher key={`partner-${p.id}-vouch-${v.level}`} partner={p} voucher={v} copyCode={copyCode} />);
                })}

                {dashboardMember?.loyalty?.currentLevel === LoyaltyLevelEnum.LEVEL3 ? (
                    <ContactFormVoucher currentLevel={dashboardMember.loyalty.currentLevel} />
                ) : null}
            </div>
        </div>
    );
};

interface PartnerVoucherProps {
    partner: VouchersPartnerView;
    voucher: VoucherView;
    copyCode: (txt: string) => void;
}

const PartnerVoucher = (props: PartnerVoucherProps): ReactElement => {
    const { t } = useTranslation('myutmb');
    const { partner, voucher, copyCode } = props;
    const { getToken } = useUser();
    const { refreshMember } = useDashboard();
    const { tenantId } = useTenancy();
    const [isGenerating, setGenerating] = useState(false);

    const handleAskVoucher = async (partner: VouchersPartnerView): Promise<void> => {
        setGenerating(true);
        const token = await getToken();
        const dto = {
            partnerId: partner.id,
        };

        const options: any = {
            method: FetchMethodsEnum.PUT,
            headers: {
                'content-type': 'application/json',
                [TENANCY_HTTP_HEADER]: tenantId,
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(dto),
        };
        await fetch(`${process.env.NEXT_PUBLIC_FRONTEND_API_URL}/users/loyalty/voucher`, options)
            .then((response) => {
                if (response.status === 200) {
                    refreshMember();
                } else if (response.status === 404) {
                    toast.error(t('myUtmb.vouchers.noMoreCode', { name: partner.name }));
                } else {
                    toast.error(`Sorry, something went wrong`);
                }
            })
            .catch((e) => {
                console.error(e);
                toast.error(`Sorry, something went wrong...`);
            })
            .finally(() => setGenerating(false));
    };

    const handleCancelExperience = async (partner: VouchersPartnerView, level: number): Promise<void> => {
        setGenerating(true);
        const token = await getToken();
        const partnerId = partner.id;

        const options: any = {
            method: FetchMethodsEnum.DELETE,
            headers: {
                'content-type': 'application/json',
                [TENANCY_HTTP_HEADER]: tenantId,
                Authorization: `Bearer ${token}`,
            },
        };
        await fetch(`${process.env.NEXT_PUBLIC_FRONTEND_API_URL}/users/loyalty/voucher/${partnerId}/${level}`, options)
            .then((response) => {
                if (response.status === 200) {
                    refreshMember();
                } else {
                    toast.error(`Sorry, something went wrong`);
                }
            })
            .catch((e) => {
                console.error(e);
                toast.error(`Sorry, something went wrong...`);
            })
            .finally(() => setGenerating(false));
    };

    const handleCopyCode = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>): void => {
        e.preventDefault();
        const el = e.currentTarget;
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection()!;
        sel.removeAllRanges();
        sel.addRange(range);
        copyCode(voucher.code);
        toast.success(t('myUtmb.vouchers.copied'));
    };

    return (
        <div className={styles.partner}>
            <div className={styles.header}>
                <div className={styles.background}>
                    {partner.logo ? (
                        <Image
                            image={pIdToCloudinary(`/assets/loyalty/${partner.logo}/banner`, partner.name)}
                            objectFit="cover"
                            layout="fill"
                            addOptions={[CloudinaryTransformEnum.TRIM]}
                        />
                    ) : null}
                </div>
                <div className={styles.wrapLogo}>
                    <div className={styles.logo}>
                        {partner.logo && partner.type !== LoyaltyPartnerTypeEnum.OPTIN ? (
                            <Image
                                image={pIdToCloudinary(`/assets/loyalty/${partner.logo}/logo`, partner.name)}
                                objectFit="contain"
                                layout="fill"
                                addOptions={[CloudinaryTransformEnum.TRIM]}
                                maxWidth={100}
                            />
                        ) : (
                            <Font.FuturaBold color={getColorStyle(ColorTypesEnum.WHITE)} as="span">
                                {partner.name}
                            </Font.FuturaBold>
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.voucher}>
                {partner.type === LoyaltyPartnerTypeEnum.EXPERIENCE_MAJOR_FINAL || partner.type === LoyaltyPartnerTypeEnum.OPTIN ? (
                    <>
                        <div className={styles.discount}>
                            <Font.OxaniumMedium desktop="14" mobile="14">
                                {partner.type === LoyaltyPartnerTypeEnum.OPTIN ? t('myUtmb.vouchers.insiders') : t('myUtmb.experiencesMajorFinal.explanations')}
                            </Font.OxaniumMedium>
                        </div>
                        <div className={styles.actions}>
                            {isGenerating ? (
                                <Spinner />
                            ) : !voucher.code ? (
                                <Link.Button
                                    href="#"
                                    label={t('myUtmb.experiencesMajorFinal.cta', { name: partner.name })}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleAskVoucher(partner);
                                    }}
                                    className={styles.button + ' experienceMajorFinal'}
                                    alignEnd={false}
                                />
                            ) : (
                                <>
                                    <div className={styles.expStatus}>
                                        <Font.OxaniumBold desktop="14" mobile="14" color={getColorStyle(ColorTypesEnum.WHITE)} as="span">
                                            {t('myUtmb.experiencesMajorFinal.imIn')}
                                        </Font.OxaniumBold>
                                    </div>
                                    <Link.Button
                                        href="#"
                                        label={t('myUtmb.experiencesMajorFinal.ctaCancel', { name: partner.name })}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleCancelExperience(partner, voucher.level);
                                        }}
                                        className={styles.button + ' ' + styles.cancelButton + ' cancelExperienceMajorFinal'}
                                        alignEnd={false}
                                    />
                                </>
                            )}
                        </div>
                    </>
                ) : null}

                {partner.type === LoyaltyPartnerTypeEnum.LIMITED_DISCOUNT || partner.type === LoyaltyPartnerTypeEnum.VOUCHER ? (
                    <>
                        <div className={styles.discount}>
                            <Font.OxaniumMedium desktop="14" mobile="14">
                                {voucher.discount && (voucher.code || partner.available)
                                    ? t('myUtmb.vouchers.discount', { discount: voucher.discount, name: partner.name })
                                    : t('myUtmb.vouchers.noMoreCode', { name: partner.name })}
                            </Font.OxaniumMedium>
                            {/*voucher.discount && (voucher.code || partner.available) ? (
                                <Font.OxaniumMedium desktop="12" mobile="12">
                                    {t('myUtmb.vouchers.discountRules.all', { name: partner.name })}
                                </Font.OxaniumMedium>
                            ) : null}
                            {voucher.discount && voucher.code ? (
                                <Font.OxaniumMedium desktop="12" mobile="12">
                                    {t('myUtmb.vouchers.discountRules.withCode', { name: partner.name })}
                                </Font.OxaniumMedium>
                            ) : null*/}
                        </div>
                        {voucher.discount && (
                            <>
                                <div className={styles.wrapExplanations}>
                                    <Icon variant={IconsEnum.HELP} color={getColorStyle(ColorTypesEnum.WHITE)} width={24} height={24} />
                                    <div className={styles.explanations}>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: partner.message ? partner.message : t('myUtmb.vouchers.explanations', { name: partner.name }),
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {partner.type === LoyaltyPartnerTypeEnum.LIMITED_DISCOUNT && partner.available && !voucher.code ? (
                            <div className={styles.actions}>
                                <div className={styles.expStatus + ' ' + styles.limited}>
                                    <Font.OxaniumBold desktop="14" mobile="14" color={getColorStyle(ColorTypesEnum.WHITE)} as="span">
                                        {t('myUtmb.vouchers.remaining', { value: partner.available, count: partner.available })}
                                    </Font.OxaniumBold>
                                </div>
                            </div>
                        ) : null}

                        {voucher.discount && (voucher.code || partner.available) ? (
                            <div className={styles.actions}>
                                {isGenerating ? (
                                    <Spinner />
                                ) : !voucher.code ? (
                                    <Link.Button
                                        href="#"
                                        label={t('myUtmb.vouchers.askButton', { name: partner.name })}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleAskVoucher(partner);
                                        }}
                                        className={styles.button + ' askVoucher'}
                                        alignEnd={false}
                                    />
                                ) : (
                                    <div className={styles.code_cont}>
                                        <a
                                            href="#"
                                            className={styles.code + ' copyVoucher'}
                                            onClick={(e) => {
                                                handleCopyCode(e);
                                            }}
                                            onKeyDown={(e) => {
                                                e.preventDefault();
                                                if (e.keyCode === 13) {
                                                    handleCopyCode(e);
                                                }
                                            }}
                                        >
                                            {voucher.code}
                                        </a>
                                        <div className={styles.date}>
                                            {voucher.expireDate ? t('myUtmb.vouchers.expire', { expire: voucher.expireDate }) : <>&nbsp;</>}
                                        </div>
                                        {partner.link && (
                                            <Link.Button
                                                className={styles.button + ' voucherPartnerLink'}
                                                href={partner.link}
                                                label={t('myUtmb.vouchers.link')}
                                                target="_blank"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </>
                ) : null}

                {partner.type === LoyaltyPartnerTypeEnum.DOWNLOAD_ASSET ? (
                    <>
                        <Font.OxaniumMedium className={styles.message} desktop="14" mobile="14">
                            {partner.message ? (
                                <span dangerouslySetInnerHTML={{ __html: partner.message }} />
                            ) : (
                                t('myUtmb.vouchers.download.description', { name: partner.name })
                            )}
                        </Font.OxaniumMedium>

                        {partner.link ? (
                            <Link.Button
                                href={partner.link}
                                target="_blank"
                                label={t('myUtmb.vouchers.download.cta')}
                                className={styles.button + ' downloadCta'}
                                alignEnd={false}
                            />
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );
};

const ContactFormVoucher = ({ currentLevel }: { currentLevel: LoyaltyLevelEnum }): ReactElement => {
    const { t } = useTranslation('myutmb');
    const [displayFormPopin, setDisplayFormPopin] = useState<boolean>(false);

    return (
        <div className={styles.partner}>
            <div className={styles.header}>
                <div className={styles.background}>
                    <Image
                        image={pIdToCloudinary(`/assets/loyalty/contact/banner`, 'contact')}
                        objectFit="cover"
                        layout="fill"
                        addOptions={[CloudinaryTransformEnum.TRIM]}
                    />
                </div>
                <div className={styles.wrapLogo}>
                    <div className={styles.logo}>
                        <Font.FuturaBold color={getColorStyle(ColorTypesEnum.WHITE)} as="span">
                            {t('myUtmb.dedicatedContact.title1')} {t('myUtmb.dedicatedContact.title2')}
                        </Font.FuturaBold>
                    </div>
                </div>
            </div>
            <div className={styles.voucher}>
                <div className={styles.discount}>
                    <Font.OxaniumMedium desktop="14" mobile="14">
                        {t('myUtmb.dedicatedContact.description')}
                    </Font.OxaniumMedium>
                </div>
                <div className={styles.wrapButtonCustom}>
                    <ButtonRemake
                        variant="solid"
                        customClassName={styles.buttonCustom + ' dedicatedContact'}
                        action={() => setDisplayFormPopin(true)}
                        label={t('myUtmb.dedicatedContact.cta')}
                    />
                </div>
            </div>
            <HubspotContactFormModal isOpen={displayFormPopin} close={() => setDisplayFormPopin(false)} currentLevel={currentLevel} />
        </div>
    );
};

export default MyLoyaltyVouchers;
